// src/components/Studio/CastingCallCard.tsx
import { Link } from 'react-router-dom';
import { Banknote, CalendarClock, Globe, MapPin } from 'lucide-react';
import type { CastingCallSummary } from '../../types/studio';
import { PANEL, PANEL_HOVER } from './studioStyles';

const CALL_TYPE_TONE: Record<string, string> = {
  CAST: 'border-orange-500/30 bg-orange-500/15 text-orange-200',
  CREW: 'border-sky-500/30 bg-sky-500/15 text-sky-200',
  COLLABORATOR: 'border-violet-500/30 bg-violet-500/15 text-violet-200',
};

/** "2026-09-14" → "14 Sep 2026". Returns null for missing or unparseable dates. */
function formatDeadline(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const CastingCallCard = ({ call }: { call: CastingCallSummary }) => {
  const deadline = formatDeadline(call.submissionDeadline);
  const typeTone = CALL_TYPE_TONE[call.callType] ?? CALL_TYPE_TONE.CAST;

  return (
    <Link
      to={`/casting/${call.slug}`}
      className={`group block p-6 ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${typeTone}`}
        >
          {call.callTypeLabel}
        </span>
        <span className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-neutral-400">
          {call.departmentLabel}
        </span>
        {!call.acceptingApplications && (
          <span className="rounded-full border border-neutral-600/40 bg-neutral-700/20 px-2.5 py-1 text-[11px] text-neutral-400">
            Closed
          </span>
        )}
      </div>

      <h3 className="mt-4 font-display text-lg font-bold leading-snug text-white transition-colors group-hover:text-orange-300">
        {call.title}
      </h3>

      {call.productionTitle && (
        <p className="mt-1 text-xs uppercase tracking-wider text-neutral-500">
          For {call.productionTitle}
        </p>
      )}

      {call.roleSummary && (
        <p className="mt-3 text-sm leading-relaxed text-neutral-400 line-clamp-3">
          {call.roleSummary}
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-neutral-500">
        {call.location && (
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            {call.location}
          </span>
        )}
        {call.remoteFriendly && (
          <span className="inline-flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" aria-hidden="true" />
            Remote friendly
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <Banknote className="h-3.5 w-3.5" aria-hidden="true" />
          {call.compensationSummary || (call.paid ? 'Paid' : 'Expenses only')}
        </span>
        {deadline && (
          <span className="inline-flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
            Closes {deadline}
          </span>
        )}
      </div>
    </Link>
  );
};

export default CastingCallCard;
